import clsx from 'clsx';
import { useEditorRef } from 'platejs/react';
import { useDropdown } from '../../hooks/useDropdown';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { insertColumnGroup, insertColumn, toggleColumnGroup } from '@platejs/layout';
import styles from './ColumnInsertMenu.module.scss';
import toolbarStyles from '../PlateToolbar/PlateToolbar.module.scss';

// Walks upward from the current selection looking for a column / column_group
// ancestor. Returns the entries (or null) so callers can act on them.
function getColumnContext(editor: any) {
    try {
        const columnEntry = editor?.api?.above?.({ match: (n: any) => n?.type === 'column' });
        const groupEntry = editor?.api?.above?.({ match: (n: any) => n?.type === 'column_group' });
        return { columnEntry, groupEntry };
    } catch {
        return { columnEntry: null, groupEntry: null };
    }
}

export function ColumnInsertMenu() {
    const editor = useEditorRef();
    const { open, setOpen, close, ref: wrapRef } = useDropdown();

    const insertColumns = (columns: number) => (e: React.MouseEvent) => {
        e.preventDefault();
        try {
            (insertColumnGroup as any)(editor, { columns });
        } catch {
            /* defensive */
        }
        close();
    };

    const insertColumnAtSide = (side: 'left' | 'right') => (e: React.MouseEvent) => {
        e.preventDefault();
        try {
            const ed: any = editor;
            const { columnEntry } = getColumnContext(ed);
            if (!columnEntry) return;
            const [, columnPath] = columnEntry;
            const last = columnPath[columnPath.length - 1];
            const targetIndex = side === 'right' ? last + 1 : last;
            const insertPath = [...columnPath.slice(0, -1), targetIndex];
            (insertColumn as any)(ed, { at: insertPath });
        } catch {
            /* defensive */
        }
        close();
    };

    const removeGroup = (e: React.MouseEvent) => {
        e.preventDefault();
        try {
            const ed: any = editor;
            const { groupEntry } = getColumnContext(ed);
            if (!groupEntry) return;
            const [, groupPath] = groupEntry;
            // Collapsing to a single column triggers withColumn's
            // "less than 2 columns" branch which unwraps the group.
            (toggleColumnGroup as any)(ed, { at: groupPath, columns: 1 });
        } catch {
            /* defensive */
        }
        close();
    };

    const ed: any = editor;
    const { columnEntry, groupEntry } = getColumnContext(ed);
    const insideColumn = Boolean(columnEntry);
    const insideGroup = Boolean(groupEntry);

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={clsx(toolbarStyles.btn, open && toolbarStyles.btnActive)}
                onMouseDown={(e) => {
                    e.preventDefault();
                    setOpen((o) => !o);
                }}
                title="Insert columns"
                aria-label="Insert columns"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                <ViewColumnIcon fontSize="small" />
                <ArrowDropDownRoundedIcon className={styles.caret} fontSize="small" aria-hidden />
            </button>
            {open && (
                <div className={styles.popover} role="menu" aria-label="Column options">
                    <button type="button" className={styles.item} role="menuitem" onMouseDown={insertColumns(2)}>
                        2 columns
                    </button>
                    <button type="button" className={styles.item} role="menuitem" onMouseDown={insertColumns(3)}>
                        3 columns
                    </button>
                    <div className={styles.divider} aria-hidden />
                    <button
                        type="button"
                        className={clsx(styles.item, !insideColumn && styles.itemDisabled)}
                        role="menuitem"
                        disabled={!insideColumn}
                        onMouseDown={insertColumnAtSide('left')}
                    >
                        Insert column left
                    </button>
                    <button
                        type="button"
                        className={clsx(styles.item, !insideColumn && styles.itemDisabled)}
                        role="menuitem"
                        disabled={!insideColumn}
                        onMouseDown={insertColumnAtSide('right')}
                    >
                        Insert column right
                    </button>
                    <div className={styles.divider} aria-hidden />
                    <button
                        type="button"
                        className={clsx(styles.item, !insideGroup && styles.itemDisabled, styles.itemDanger)}
                        role="menuitem"
                        disabled={!insideGroup}
                        onMouseDown={removeGroup}
                    >
                        Remove column group
                    </button>
                </div>
            )}
        </div>
    );
}
